import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { Accordion as BootstrapAccordion } from 'react-bootstrap'
import './Accordion.module.scss'

const propTypes = {
  /**
   * Tabs of the Accordion
   */
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      content: PropTypes.node.isRequired,
    }),
  ).isRequired,
  /**
   * Accordion style
   */
  appearance: PropTypes.oneOf(['block', 'clean']),
  /**
   * If true, display open/close arrow in the Accordion tab header
   */
  showArrow: PropTypes.bool,
  /**
   * Position of the arrow in the header
   */
  arrowPosition: PropTypes.oneOf(['start', 'end']),
  /**
   * If true, accordion will be collapsed first time
   */
  defaultCollapsed: PropTypes.bool,
  /**
   * If true, highlights opened tab header
   */
  highlightOpenedTab: PropTypes.bool,
}

/**
 * Accordion (react-bootstrap 2 / Bootstrap 5): one stacked accordion per tab so multiple panels can stay open.
 */
function Accordion(props) {
  const {
    tabs,
    appearance = 'block',
    showArrow = false,
    defaultCollapsed = false,
    arrowPosition = 'end',
    highlightOpenedTab = false,
  } = props ?? {}

  return (
    <div>
      {tabs.map(({ id, label, content }) => (
        <AccordionTab
          key={id}
          id={id}
          label={label}
          content={content}
          appearance={appearance}
          showArrow={showArrow}
          defaultCollapsed={defaultCollapsed}
          arrowPosition={arrowPosition}
          highlightOpenedTab={highlightOpenedTab}
        />
      ))}
    </div>
  )
}

/* eslint-disable react/prop-types */
function AccordionTab(props) {
  const { id, label, content, appearance, showArrow, defaultCollapsed, arrowPosition, highlightOpenedTab } = props

  const [activeKey, setActiveKey] = useState(defaultCollapsed ? null : id)

  return (
    <BootstrapAccordion
      activeKey={activeKey}
      onSelect={setActiveKey}
      className={clsx('Uic-Accordion-base', `Uic-Accordion-${appearance}`)}
    >
      <BootstrapAccordion.Item
        eventKey={id}
        className={clsx(highlightOpenedTab && activeKey === id && 'Uic-Accordion-highlightHeader')}
      >
        <BootstrapAccordion.Header
          as="div"
          className={clsx(
            'Uic-Accordion-headerInner',
            arrowPosition === 'end' ? 'Uic-Accordion-headerInner--arrowEnd' : 'Uic-Accordion-headerInner--arrowStart',
          )}
        >
          {showArrow && arrowPosition === 'start' && <Arrow open={activeKey === id} position={arrowPosition} />}
          {label}
          {showArrow && arrowPosition === 'end' && <Arrow open={activeKey === id} position={arrowPosition} />}
        </BootstrapAccordion.Header>
        <BootstrapAccordion.Body>{content}</BootstrapAccordion.Body>
      </BootstrapAccordion.Item>
    </BootstrapAccordion>
  )
}

function Arrow(props) {
  const { open, position } = props
  return (
    <div
      className={clsx(
        'd-flex align-items-center',
        position === 'end' ? 'justify-content-end' : 'justify-content-start',
        'Uic-Accordion-controls',
      )}
    >
      <i className={`Uic-Accordion-arrow fas fa-chevron-${open ? 'down' : 'right'}`} />
    </div>
  )
}

Accordion.propTypes = propTypes

export default Accordion
